import type { AccountingStartResult, Organization } from '@/api/models';
import { mapFiscalYear } from '../periods/periodsMapping';
import type { AccountingStartWire, OrganizationWire } from './responses';

/**
 * Wire ⇄ domain for the organisation. Timestamps stay ISO strings, as every
 * other resource keeps them; the fiscal year goes through the periods mapping,
 * because accounting start returns the same `FiscalYearResponse` the periods
 * endpoints do.
 */

export const toOrganization = (wire: OrganizationWire): Organization => ({
    id: wire.id,
    name: wire.name,
    onboardedAt: wire.onboardedAt ?? null,
    accountingStartNeeded: wire.accountingStartNeeded,
});

export const toAccountingStartResult = (
    wire: AccountingStartWire,
): AccountingStartResult => ({
    fiscalYear: wire.fiscalYear ? mapFiscalYear(wire.fiscalYear) : null,
    taxRatesReady: wire.taxRatesReady,
});

// ── error codes ─────────────────────────────────────────────────────────────

/**
 * Every refusal the organisation endpoints produce themselves, from
 * `OrganizationErrorCodes` in the API. A failed call rejects with the
 * `ErrorResponse`, whose `code` is one of these.
 *
 * Accounting start also passes the fiscal-year and tax-settings services'
 * refusals through with their own codes (a conflict as 409), so a caller that
 * only knows this list must still fall back to the server's message.
 */
export const ORGANIZATION_ERROR_CODES = [
    /** 404: the token's organisation no longer exists; sign in again. */
    'ORGANIZATION_NOT_FOUND',
    /** 400: accounting start named no year, or one outside 2000–2100. */
    'YEAR_INVALID',
    /** 400: every period of the named fiscal year is closed or locked. */
    'FISCAL_YEAR_CLOSED',
] as const;

export type OrganizationErrorCode = (typeof ORGANIZATION_ERROR_CODES)[number];

export const isOrganizationErrorCode = (
    raw: unknown,
): raw is OrganizationErrorCode =>
    typeof raw === 'string' &&
    (ORGANIZATION_ERROR_CODES as readonly string[]).includes(raw);
